import { useEffect, useState } from "react";
import AppShell from "../components/app/AppShell";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { Sparkle, ArrowsClockwise } from "@phosphor-icons/react";

export default function StudyPlanPage() {
  const { t, tDomain } = useT();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/study-plan");
      setPlan(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const regen = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/study-plan/regenerate");
      setPlan(data);
      toast.success(t("plan.refreshed"));
    } catch {
      toast.error(t("plan.could_not_refresh"));
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#002FA7]">{t("plan.overline")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("plan.title")}</h1>
          <p className="text-slate-600 mt-2 max-w-xl">{t("plan.subtitle")}</p>
        </div>
        <button className="btn-ai" onClick={regen} disabled={regenerating} data-testid="plan-regenerate-button">
          <ArrowsClockwise size={16} weight="bold" /> {regenerating ? t("plan.generating") : t("plan.regenerate")}
        </button>
      </div>

      {loading ? (
        <div className="h-40 mt-8 grid place-items-center" data-testid="plan-loading">
          <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin" />
        </div>
      ) : !plan ? (
        <div className="card mt-8 text-center">
          <div className="overline text-[#002FA7]"><Sparkle size={12} weight="fill" className="inline" /></div>
          <h3 className="font-display text-xl font-semibold mt-2">{t("plan.no_plan")}</h3>
          <p className="text-slate-600 mt-2">{t("plan.no_plan_sub")}</p>
          <button className="btn-primary mt-6" onClick={regen} disabled={regenerating} data-testid="plan-generate-first">
            {regenerating ? t("plan.generating") : t("plan.generate")}
          </button>
        </div>
      ) : (
        <>
          {plan.rationale && (
            <div className="ai-callout mt-6" data-testid="plan-rationale">
              <div className="overline text-[#002FA7]"><Sparkle size={12} weight="fill" className="inline" /> {t("plan.rationale")}</div>
              <div className="text-sm mt-1 text-slate-700">{plan.rationale}</div>
            </div>
          )}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4" data-testid="plan-days-grid">
            {plan.days.map((d, i) => (
              <div key={i} className="card card-hover">
                <div className="overline">{d.day}</div>
                <div className="font-display text-base font-bold mt-1">{new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                <div className="mt-3 text-sm font-semibold text-[#002FA7]">{tDomain(d.focus_domain)}</div>
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  {d.activities.map((a, k) => <li key={k}>· {a}</li>)}
                </ul>
                <div className="mt-3 text-xs text-slate-500">{t("plan.target")} <span className="font-mono font-bold text-slate-900">{d.target_questions}</span> {t("plan.target_suffix")}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}
